import React from 'react';
import '../styles/ExperienceSection.css';

const experiences = [
  // NEW: Grainger entry (current role)
  {
    companyName: "Grainger",
    period: "(2024 – Present)",
    roleTitle: "Solution Architect – Warehouse Automation",
    roleDescription:
      "Guiding architecture strategy and modernization for Grainger’s next-generation automated distribution centers, bridging IT and OT systems to enable scalable, secure, and resilient operations.",
    achievements: [
      "Leading solution architecture for next-generation distribution centers, integrating IT and OT systems, warehouse automation, and robotics within a secure, scalable hybrid architecture.",
      "Architecting IT/OT-integrated warehouse ecosystems leveraging SAP EWM and warehouse execution, robotic picking and packing, conveyance, and automated packaging systems (LogistiVIEW, Exotec, Sparck), supported by industrial infrastructure and data platforms (Ignition, Octoplant), to deliver scalable, resilient, and orchestrated automation across Grainger’s distribution network.",
      "Advancing predictive monitoring and observability capabilities across Warehouse Execution and automation platforms, leveraging AppDynamics and complementary tools to deliver end-to-end visibility, proactive issue detection, and improved operational resilience.",
      "Collaborating with Integration, Infrastructure, Security, and Network teams to strengthen governance practices and advance architecture maturity by defining common deliverables, standard templates, and RACI alignment, while improving end-to-end solution design documentation and handoffs to execution teams.",
      "Shaping repeatable architecture patterns for remote access, hybrid managed services, and vendor integrations, enabling secure, scalable, and operationally sustainable solutions across IT and OT environments.",
      "Defining and publishing architecture standards to drive design consistency and solution compliance across a multi-vendor IT/OT environment, strengthening governance and interoperability across the warehouse automation ecosystem."
    ],
  },

  // Rivian – Senior IT Ops Manager
  {
    companyName: "Rivian, Normal IL",
    period: "(Oct 2021 - Nov 2023)",
    roleTitle: "Senior IT Operations Manager",
    roleDescription:
      "Led the inception of Rivian's flagship plant IT operations, skillfully integrating advanced technology with business objectives. My leadership steered the company through operational improvements in IT efficiency, system robustness, and team dynamism.",
    achievements: [
      "Orchestrated the launch of plant IT operations, merging technology and business acumen to significantly enhance manufacturing efficiency.",
      "Created the SRE landscape, enhancing system observability and refining CI/CD pipelines for improved IT operational excellence.",
      "Significantly reduced downtime and blast radius from hours to minutes per week through re-design of MES system architecture.",
      "Led a dynamic team of 60, fostering a culture of continuous operational excellence and collaboration in software and infrastructure.",
      "Implemented a cost-saving strategy by transitioning to managed services, saving the company $1.5M and enhancing system support capabilities."
    ],
  },

  // Rivian – Lead Enterprise Architect
  {
    companyName: "Rivian, Normal IL",
    period: "(Jun 2019 - Dec 2021)",
    roleTitle: "Lead Enterprise Architect",
    roleDescription:
      "Guided IT architecture at Rivian, aligning enterprise frameworks with business objectives. Developed IT strategies, improving agility and resilience.",
    achievements: [
      "Developed a comprehensive IT strategy that bridged technology capabilities with business goals that enabled the company to achieve critical functions to go public and manufacture hundreds of vehicles per day.",
      "Introduced streamlined cloud and on-prem solution architectures for enterprise and factory operations, enhancing agility and system resilience.",
      "Sculpted a business-centric IT capability model and value stream maps to align technology with evolving enterprise strategies.",
      "Pioneered rapid, secure facility launch standards, embedding robust network standards and accelerating operational readiness.",
      "Designed and deployed secure IT/OT network integration, producing a repeatable, deployable architecture for current and future plants."
    ],
  },

  // Pekin – IT Director
  {
    companyName: "Pekin Insurance, Pekin IL",
    period: "(Jan 2018 - May 2019)",
    roleTitle: "IT Director",
    roleDescription:
      "Led Pekin Insurance's IT transformation with a focus on strategy, governance, and architecture maturity; improved responsiveness and efficiency across the portfolio.",
    achievements: [
      "Led transformation initiatives that improved portfolio visibility and throughput; established a common 90-day planning cadence across 30 teams.",
      "Partnered with HR to deploy a modern IT Job Architecture aligned to digital and product-centric operating models.",
      "Deployed ServiceNow for demand and portfolio management, elevating prioritization and resource allocation.",
      "Launched an IT Learning & Development program to upskill teams for modernization initiatives.",
      "Overhauled invoicing and contract processes, unlocking ~$800K in cost savings and improving financial governance."
    ],
  },

  // Pekin – Chief/Principal Architect
  {
    companyName: "(Pekin Insurance, Pekin IL)",
    period: "(Feb 2016 - Dec 2017)",
    roleTitle: "Chief Architect",
    roleDescription:
      "Directed transformative architecture projects and launched the Integration Center of Excellence, standardizing core system integrations.",
    achievements: [
      "Directed a team of 12 architects across 16 transformation projects, establishing resilient reference architectures and standards.",
      "Launched the Integration Center of Excellence to standardize patterns and frameworks for core system integration.",
      "Authored current, interim, and target-state blueprints; aligned technology roadmaps to strategic business outcomes.",
      "Program Architect for the Guidewire implementation, guiding strategic decisions and supervising development teams.",
      "Initiated the company’s Architecture Review Board, establishing governance and decision traceability."
    ],
  },

  // Caterpillar – Earlier Career Experience (no dates to reduce age bias)
  {
    companyName: "Caterpillar, Peoria IL (Earlier Career Experience)",
    period: "", // intentionally left blank to deemphasize dates
    roleTitle: "Progressive Roles in IT Architecture and Management",
    roleDescription:
      "Re-chartered enterprise architecture, established governance, and developed target-state architectures that secured investment in cloud, data, and analytics.",
    achievements: [
      "EA Portfolio & Technology Manager: Established an Enterprise Architecture playbook, target-state roadmaps, and cloud strategies; advanced analytics and cloud initiatives.",
      "Solution & Innovation Manager: Oversaw architecturally significant programs including a Mobile COE and cloud strategy.",
      "Sr. IT Supervisor: Led teams in Service Information Systems and Warranty Management; improved customer experience and delivery efficiency.",
      "Six Sigma Black Belt / IT Analyst: Delivered networking and application projects that improved operational efficiency."
    ],
  },
];

const ExperienceSection = () => (
  <div className="experience-section section">
    <h2>Experience</h2>
    {experiences.map((exp, index) => (
      <div className="experience" key={index}>
        <div className="experience-header">
          <span className="company-name">{exp.companyName} </span>
        </div>
        <h3 className="role-title">
          {exp.roleTitle}
          {exp.period && <span className="period">&nbsp;{exp.period}</span>}
        </h3>
        {exp.roleDescription && (
          <p className="role-description">{exp.roleDescription}</p>
        )}
        {Array.isArray(exp.achievements) && exp.achievements.length > 0 && (
          <ul className="achievements">
            {exp.achievements.map((achievement, i) => (
              <li key={i}>{achievement}</li>
            ))}
          </ul>
        )}
      </div>
    ))}
  </div>
);

export default ExperienceSection;
